/**
 * Answer save reconciliation (fix-q1flow F43).
 *
 * A voice answer writes the draft twice in quick succession: the transcript
 * lands first, the archived recording adds its attachment id a moment later.
 * Both writes race the debounced save, so the SECOND save could go out with
 * the revision the FIRST one had already consumed. The server answers 409
 * `answer_changed`, and the old client code read every 409 as "somebody else
 * edited this in another window" - Pavel saw the conflict banner with no other
 * window open, and "load newer version" showed him his own text back.
 *
 * The truth is simple: a revision mismatch is only a conflict when the answer
 * ON THE SERVER differs from what we tried to save. If the server already
 * holds exactly our payload, our own write landed and the only thing that is
 * stale is our revision counter - adopt it and stay quiet.
 */

export type AnswerConflictOutcome =
  | { kind: "already_saved"; revision: number }
  | { kind: "conflict"; serverRevision: number }
  | { kind: "error" };

export interface AnswerConflictInput {
  localRevision: number;
  sent: { value: Record<string, unknown>; attachmentId: string | null };
  server: {
    revision?: number;
    value?: Record<string, unknown> | null;
    attachmentId?: string | null;
  };
}

/**
 * Key order must not decide whether two answers are the same: the value makes
 * a round trip through JSON on the server, and nested objects can come back
 * ordered differently than we sent them.
 */
export function canonicalAnswerJson(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonicalAnswerJson).join(",")}]`;
  if (value && typeof value === "object") {
    return `{${Object.entries(value as Record<string, unknown>)
      .filter(([, entry]) => entry !== undefined)
      .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
      .map(([key, entry]) => `${JSON.stringify(key)}:${canonicalAnswerJson(entry)}`)
      .join(",")}}`;
  }
  return JSON.stringify(value ?? null);
}

export function sameAnswerPayload(
  left: { value: Record<string, unknown> | null | undefined; attachmentId: string | null | undefined },
  right: { value: Record<string, unknown> | null | undefined; attachmentId: string | null | undefined }
): boolean {
  if ((left.attachmentId ?? null) !== (right.attachmentId ?? null)) return false;
  return canonicalAnswerJson(left.value ?? null) === canonicalAnswerJson(right.value ?? null);
}

/** Kept for the calm-save contract: a bare revision comparison, no payload. */
export function classifyAnswerConflict(
  localRevision: number,
  serverRevision: number | undefined
): "conflict" | "error" {
  return typeof serverRevision === "number" && serverRevision !== localRevision ? "conflict" : "error";
}

export function resolveAnswerConflict(input: AnswerConflictInput): AnswerConflictOutcome {
  const serverRevision = input.server.revision;
  if (typeof serverRevision !== "number" || serverRevision === input.localRevision) return { kind: "error" };
  // The server did not tell us what it holds (older worker): fall back to the
  // revision-only reading rather than inventing a verdict.
  if (input.server.value === undefined) return { kind: "conflict", serverRevision };
  if (sameAnswerPayload(input.sent, { value: input.server.value, attachmentId: input.server.attachmentId ?? null })) {
    return { kind: "already_saved", revision: serverRevision };
  }
  return { kind: "conflict", serverRevision };
}
